import { CustomCode } from '@betterfunnel/types';
import AlertModal from '@betterfunnel/ui-core/AlertModal';
import Button from '@betterfunnel/ui-core/Button';
import { updateCustomCode } from '@betterfunnel/utils';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DashboardContext } from '../../contexts/DashboardContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import SettingSectionTitle from '../settings/SettingSectionTitle';
import CustomCodeCard from './CustomCodeCard';
import CustomCodeEditorModal from './CustomCodeEditorModal';

export const AccountCustomCodes = () => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { accountConfig, updateAccountConfig } = useContext(DashboardContext);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<CustomCode | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<string | undefined>();

  const accountCodes = useMemo(
    () => accountConfig?.customCodes || [],
    [accountConfig?.customCodes],
  );

  const updateCodes = useCallback(
    async (codes: CustomCode[]) => {
      try {
        await updateAccountConfig({
          customCodes: codes as any,
        });
        show('Updated');
      } catch (error) {
        console.error(error);
        show('Failed to update');
      }
    },
    [updateAccountConfig, show],
  );

  const handleAddCode = () => {
    setEditingCode(undefined);
    setEditorOpen(true);
  };

  const handleEditCode = (code: CustomCode) => {
    setEditingCode(code);
    setEditorOpen(true);
  };

  const handleSaveCode = useCallback(
    async (code: CustomCode) => {
      const isNew = !accountCodes.find((c) => c.id === code.id);

      if (isNew) {
        // Add new code
        await updateCodes([...accountCodes, code]);
      } else {
        // Update existing code
        const updatedCodes = accountCodes.map((c) =>
          c.id === code.id ? code : c,
        );
        await updateCodes(updatedCodes);
      }
    },
    [accountCodes, updateCodes],
  );

  const handleToggleCode = useCallback(
    async (codeId: string, enabled: boolean) => {
      const updatedCodes = accountCodes.map((c) =>
        c.id === codeId ? updateCustomCode(c, { enabled }) : c,
      );
      await updateCodes(updatedCodes);
    },
    [accountCodes, updateCodes],
  );

  const handleDeleteCode = useCallback(
    async (codeId: string) => {
      const updatedCodes = accountCodes.filter((c) => c.id !== codeId);
      await updateCodes(updatedCodes);
      setDeleteConfirm(undefined);
    },
    [accountCodes, updateCodes],
  );

  return (
    <div>
      <SettingSectionTitle
        title="Custom Codes (Advanced)"
        description="Manage multiple custom code snippets at the account level. These codes will be inherited by all campaigns and websites."
      />

      <div className="mt-6 border-t border-gray-200 pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">
            Account Custom Codes
          </h3>
          <Button size="small" onClick={handleAddCode}>
            <PlusIcon className="-ml-0.5 h-4 w-4" />
            Add Code
          </Button>
        </div>

        {accountCodes.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <p className="text-sm text-gray-500">
              No custom codes yet. Add your first custom code to get started.
              These codes will be automatically inherited by all campaigns and
              websites.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {accountCodes.map((code) => (
              <CustomCodeCard
                key={code.id}
                customCode={code}
                source="account"
                displayEnabled={code.enabled}
                onToggle={handleToggleCode}
                onEdit={handleEditCode}
                onDelete={(id) => setDeleteConfirm(id)}
              />
            ))}
          </div>
        )}
      </div>

      <CustomCodeEditorModal
        customCode={editingCode}
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingCode(undefined);
        }}
        onSave={handleSaveCode}
        userId={selectedAccount?.data().createdBy || 'unknown'}
      />

      <AlertModal
        title="Delete Custom Code"
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setDeleteConfirm(undefined),
          },
          {
            type: 'primary',
            color: 'negative',
            title: 'Delete',
            onClick: () => deleteConfirm && handleDeleteCode(deleteConfirm),
          },
        ]}
        type="warn"
      >
        <p>
          Are you sure you want to delete this custom code? This will affect all
          campaigns and websites that inherit this code. This action cannot be
          undone.
        </p>
      </AlertModal>
    </div>
  );
};
